import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../styles/ThemeContext';
import { FiBook, FiSearch, FiChevronRight, FiChevronDown, FiX } from 'react-icons/fi';

// ── Guide content structured from USER_GUIDE.md ──
const GUIDE_SECTIONS = [
  {
    id: 'login',
    title: '1. Logging In',
    content: [
      { type: 'ol', items: [
        'Open the application in your browser',
        'Enter your **username** and **password**',
        'Click **Login**'
      ]},
      { type: 'note', text: 'Your login credentials are created by an administrator via the Admin Panel.' }
    ]
  },
  {
    id: 'navigation',
    title: '2. Main Screen & Navigation',
    content: [
      { type: 'text', text: 'After logging in, you will see the header with the following elements:' },
      { type: 'table', headers: ['Element', 'Function'], rows: [
        ['TMS Logo + Title', 'Application name'],
        ['Step 1–5 Indicators', 'Shows your current step and which steps are completed'],
        ['Guides', 'Guidelines and User Guide'],
        ['My Orders', 'View your orders and drafts (with preview and print)'],
        ['Save draft', 'Save work in progress without confirming — visible from Step 1 onwards'],
        ['Theme Toggle', 'Switch between Day Mode and Night Mode'],
        ['Username', 'Shows who is currently logged in'],
        ['Admin', 'Only visible to administrators — opens the Admin Panel'],
        ['Logout', 'Log out and return to the login screen']
      ]}
    ]
  },
  {
    id: 'packing-list',
    title: '3. Creating a Packing List',
    content: [
      { type: 'text', text: 'Creating a packing list is done through 5 sequential steps. You can navigate back to a previous step at any time to make changes. Use Save draft in the header to save your progress and continue later.' }
    ],
    subsections: [
      {
        id: 'step1',
        title: 'Step 1: Addresses & Project Information',
        content: [
          { type: 'text', text: 'Fill in the collection and delivery addresses, along with project details.' },
          { type: 'heading', text: 'Collection Address' },
          { type: 'list', items: ['Company name (required)', 'Address, Postal code, City', 'Country (selected from country list)', 'Contact person, Phone, Email'] },
          { type: 'heading', text: 'Delivery Address' },
          { type: 'list', items: ['Same fields as the collection address'] },
          { type: 'heading', text: 'Project Information' },
          { type: 'list', items: ['Department #', 'Project #', 'Prepared By', 'Pickup Date', 'Delivery Date'] },
          { type: 'heading', text: 'Tips' },
          { type: 'list', items: [
            'Use the Address Book feature to quickly select previously saved addresses',
            'Save frequently used addresses via the Save button so you can reuse them later',
            'All fields marked as required must be filled in before you can proceed to Step 2'
          ]}
        ]
      },
      {
        id: 'step2',
        title: 'Step 2: Shipping Units',
        content: [
          { type: 'text', text: 'Define the physical packaging units (pallets, containers, boxes, etc.).' },
          { type: 'heading', text: 'For each shipping unit, fill in:' },
          { type: 'list', items: [
            'Type — Select from the catalog (e.g., Pallet, Container)',
            'Dimensions — Length, Width, Height (in cm) — auto-populated based on selected type',
            'Tare Weight — Weight of the packaging itself (kg)',
            'Total Weight — Including contents (kg)',
            'Export Type — Temporary or Permanent'
          ]},
          { type: 'heading', text: 'Actions' },
          { type: 'list', items: ['Add Unit — Add a new shipping unit', 'Edit — Modify an existing unit (inline editing)', 'Delete — Remove a unit from the list'] },
          { type: 'note', text: 'You can add multiple units per packing list. Each product will later be assigned to a specific unit.' }
        ]
      },
      {
        id: 'step3',
        title: 'Step 3: Products',
        content: [
          { type: 'text', text: 'Assign products to each shipping unit.' },
          { type: 'heading', text: 'Adding a product' },
          { type: 'ol', items: [
            'Click Add Product next to the desired shipping unit',
            'The Product Selection Modal opens',
            'Browse the catalog or use the search function',
            'Filter by category if needed',
            'Select the product and fill in the details:'
          ]},
          { type: 'list', items: ['EQ Number — Equipment number', 'Qty — Quantity', 'CoO — Country of Origin', 'HS Code — Customs tariff code', 'Value — Value in euros', 'DG (Dangerous Goods) — Yes/No'] },
          { type: 'heading', text: 'Product type E (Equipment)' },
          { type: 'list', items: [
            'If you add a product with type E that is already in the unit, a popup appears',
            'Enter the quantity to add — the existing product quantity is updated (not duplicated)',
            'Click Add to update, or Cancel to skip that product (other selected products are still added)'
          ]},
          { type: 'heading', text: 'Custom Products' },
          { type: 'list', items: [
            'If the product is not in the catalog, create a custom product',
            'Fill in the product details and save it for future use'
          ]},
          { type: 'heading', text: 'Dangerous Goods (DG) Warning' },
          { type: 'list', items: [
            'The system automatically warns you if you try to place DG and non-DG products in the same unit',
            'A warning modal with details will appear',
            'You can confirm the warning to proceed anyway'
          ]}
        ]
      },
      {
        id: 'step4',
        title: 'Step 4: Preview',
        content: [
          { type: 'text', text: 'This step shows a printable preview of your complete packing list.' },
          { type: 'heading', text: 'What you see' },
          { type: 'list', items: [
            'Auto-generated order number (format: PL-NNNN-DDMMYYYY)',
            'Collection and delivery addresses in a clear layout',
            'Project information',
            'Table with all shipping units and their contents',
            'Total quantities and values'
          ]},
          { type: 'heading', text: 'Actions' },
          { type: 'list', items: [
            'Print / PDF — Print the packing list or save as PDF via the browser print function',
            'Back — Go back to a previous step to make changes',
            'Continue — Proceed to the confirmation step'
          ]},
          { type: 'note', text: 'The preview is optimized for printing with a white background, regardless of your theme setting.' }
        ]
      },
      {
        id: 'step5',
        title: 'Step 5: Confirmation',
        content: [
          { type: 'text', text: 'The final step for saving your packing list.' },
          { type: 'heading', text: 'Summary' },
          { type: 'list', items: [
            'Total number of shipping units',
            'Total number of products',
            'Total quantity',
            'Total value',
            'DG indication (whether dangerous goods are present)'
          ]},
          { type: 'heading', text: 'Actions' },
          { type: 'list', items: [
            'Save draft — Save your work in progress without confirming. You can resume later via My Orders',
            'Confirm and Save — Save the packing list and mark it as confirmed. You will receive a unique order number',
            'Email Notification — An email is sent automatically after confirming (not when saving a draft)'
          ]},
          { type: 'heading', text: 'After saving' },
          { type: 'list', items: [
            'Order Saved! — Success message with your order number',
            'Print / PDF — Print the saved packing list',
            'View My Orders — Open your personal order history',
            '+ New Packing List — Start a new packing list immediately'
          ]},
          { type: 'note', text: 'Once an order is confirmed and saved, you cannot go back to edit it. If you click on a previous step (e.g. Addresses, Shipping Units), a popup will appear: "The order has been submitted. If you want to make changes or add more items, please create a new packing list." Use + New Packing List to start fresh.' }
        ]
      }
    ]
  },
  {
    id: 'admin',
    title: '4. Admin Panel',
    content: [
      { type: 'text', text: 'The Admin Panel is only accessible to users with the admin role. Click the Admin icon in the header and enter the admin password.' },
      { type: 'text', text: 'The panel contains 10 tabs:' }
    ],
    subsections: [
      {
        id: 'admin-orders',
        title: 'Orders',
        content: [
          { type: 'text', text: 'Overview of all saved packing lists (both drafts and confirmed orders). The Status column shows the current status of each order.' },
          { type: 'table', headers: ['Status', 'Description'], rows: [
            ['Draft', 'Work in progress — not yet confirmed'],
            ['Confirmed', 'Packing list saved and confirmed by the user'],
            ['Handled', 'Order has been exported to Excel — automatically set when the Excel export button is clicked']
          ]},
          { type: 'table', headers: ['Action', 'Description'], rows: [
            ['View', 'Open the details of a packing list'],
            ['Edit', 'Modify addresses, units, or products'],
            ['Delete', 'Permanently delete a packing list — a confirmation popup appears before deletion'],
            ['Excel (Both Sheets)', 'Export the order to Excel — automatically sets the status to Handled']
          ]}
        ]
      },
      {
        id: 'admin-products',
        title: 'Products',
        content: [
          { type: 'text', text: 'Manage the product catalog that users see when creating packing lists.' },
          { type: 'table', headers: ['Field', 'Description'], rows: [
            ['Category', 'Product category'],
            ['Group', 'Product group'],
            ['Product Name', 'Name of the product'],
            ['Product Type', 'E (Equipment), C, P, or U — type E products update quantity when added again to the same unit'],
            ['Country of Origin', '2-letter country code'],
            ['Manufacturer', 'Manufacturer name'],
            ['Value', 'Unit price (EUR)'],
            ['HS Code', 'Customs tariff code'],
            ['DG', 'Dangerous Goods (Yes/No)'],
            ['Export Type', 'Temporary or Permanent']
          ]},
          { type: 'text', text: 'Actions: Add, Edit (opens in modal), Delete, Search, Sort' }
        ]
      },
      {
        id: 'admin-units',
        title: 'Shipping Units',
        content: [
          { type: 'text', text: 'Manage the catalog of packaging types (pallets, containers, etc.).' },
          { type: 'table', headers: ['Field', 'Description'], rows: [
            ['Type', 'Name of the type (e.g., Pallet, Container)'],
            ['L / W / H', 'Standard dimensions in cm'],
            ['Tare', 'Standard tare weight in kg'],
            ['Export Type', 'Temporary or Permanent']
          ]}
        ]
      },
      {
        id: 'admin-addresses',
        title: 'Addresses',
        content: [
          { type: 'text', text: 'Manage the address book used in Step 1 when creating packing lists. Saved addresses can be quickly selected to auto-fill address fields.' },
          { type: 'table', headers: ['Field', 'Description'], rows: [
            ['Company', 'Company name (required)'],
            ['Address', 'Street and house number'],
            ['Postal', 'Postal code'],
            ['City', 'City name'],
            ['Country', 'Country — selected from the country list'],
            ['Contact', 'Contact person name'],
            ['Phone', 'Phone number'],
            ['E-mail', 'Email address']
          ]},
          { type: 'text', text: 'Actions: Add, Edit (opens in modal), Delete, Search' }
        ]
      },
      {
        id: 'admin-countries',
        title: 'Countries',
        content: [
          { type: 'text', text: 'Manage the country list used in address and product fields.' },
          { type: 'table', headers: ['Field', 'Description'], rows: [
            ['Code', '2-letter country code (e.g., NL, DE, US)'],
            ['Name', 'Full country name']
          ]}
        ]
      },
      {
        id: 'admin-guidelines',
        title: 'Guidelines',
        content: [
          { type: 'text', text: 'Edit the packing guidelines that users can consult while creating a packing list (via the Guidelines button in the header).' }
        ]
      },
      {
        id: 'admin-users',
        title: 'Users',
        content: [
          { type: 'text', text: 'Manage user accounts.' },
          { type: 'table', headers: ['Action', 'Description'], rows: [
            ['Create', 'Create a new account with username, password, and role'],
            ['Edit', 'Change username, role, or password'],
            ['Delete', 'Delete an account (not your own account)']
          ]},
          { type: 'heading', text: 'Roles' },
          { type: 'list', items: [
            'user — Can create packing lists',
            'admin — Can create packing lists + access to Admin Panel'
          ]}
        ]
      },
      {
        id: 'admin-login-activity',
        title: 'Login Activity',
        content: [
          { type: 'text', text: 'Overview of user login and logout sessions.' },
          { type: 'table', headers: ['Column', 'Description'], rows: [
            ['User', 'Username of the user who logged in'],
            ['Logged in at', 'Date and time of login'],
            ['Logged out at', 'Date and time of logout, or "Still logged in" if the user has not explicitly logged out'],
            ['Actions', 'Mark logged out — For sessions showing "Still logged in" (e.g. browser closed), manually mark as logged out. Delete — Remove a session from the list']
          ]},
          { type: 'note', text: 'Login and logout times are recorded automatically. If a user closes the browser without logging out, "Still logged in" is shown. Use Mark logged out to correct this when you know the user is no longer active.' }
        ]
      },
      {
        id: 'admin-export',
        title: 'Export',
        content: [
          { type: 'text', text: 'Export data to CSV files for use in Excel or other programs.' },
          { type: 'heading', text: 'Available exports' },
          { type: 'list', items: ['Products', 'Orders', 'Shipping Units'] }
        ]
      },
      {
        id: 'admin-settings',
        title: 'Settings',
        content: [
          { type: 'text', text: 'Configure application-wide settings.' },
          { type: 'heading', text: 'Email Notification' },
          { type: 'list', items: [
            'To — Set the primary email address that receives notifications when a packing list is confirmed',
            'CC — Optional carbon copy address (e.g. for a second recipient)',
            'Leave both empty to disable email notifications',
            'Changes take effect immediately — no server restart required'
          ]},
          { type: 'note', text: 'The notification email is sent automatically after each confirmed packing list in Step 5. Drafts do not trigger emails.' }
        ]
      }
    ]
  },
  {
    id: 'features',
    title: '5. Additional Features',
    subsections: [
      {
        id: 'feat-myorders',
        title: 'My Orders',
        content: [
          { type: 'text', text: 'Click the "My Orders" button in the header to view all packing lists you have created.' },
          { type: 'list', items: [
            'Only shows orders created by your account',
            'Drafts appear with a "Draft" badge — click Resume to continue editing from the step where you saved the draft',
            'Click the ✕ button on a draft to delete it — a confirmation popup appears before deletion',
            'Confirmed orders are read-only — click to view full details (addresses, units, products)',
            'Use Preview and Print / PDF for a print-ready version',
            'Also accessible via the "View My Orders" button after saving a packing list in Step 5'
          ]}
        ]
      },
      {
        id: 'feat-email',
        title: 'Email Notifications',
        content: [
          { type: 'text', text: 'When a packing list is confirmed (not when saved as draft), the system automatically sends an email notification.' },
          { type: 'list', items: [
            'A confirmation email is sent after each confirmed packing list in Step 5',
            'The email contains order details: order number, creator, units, products, value, and addresses',
            'If Dangerous Goods are present, this is highlighted in the email',
            'The To and CC addresses can be configured by an admin via Admin Panel > Settings',
            'Email delivery does not affect the save — your order is saved even if the email fails',
            'Drafts do not trigger email notifications'
          ]}
        ]
      },
      {
        id: 'feat-theme',
        title: 'Theme (Dark/Light)',
        content: [
          { type: 'text', text: 'Click the theme icon in the header to switch between dark and light theme. Your preference is remembered.' }
        ]
      },
      {
        id: 'feat-guidelines',
        title: 'Guidelines',
        content: [
          { type: 'text', text: 'Click the Guidelines button in the header to open the packing guidelines. These contain instructions and best practices for properly packing and labeling shipments.' }
        ]
      },
      {
        id: 'feat-addressbook',
        title: 'Address Book',
        content: [
          { type: 'list', items: [
            'Save addresses during Step 1 for reuse',
            'Select saved addresses to automatically fill in form fields',
            'Delete addresses you no longer need'
          ]}
        ]
      },
      {
        id: 'feat-custom',
        title: 'Custom Products',
        content: [
          { type: 'list', items: [
            'Create your own products if they are not in the standard catalog',
            'Custom products are saved and available for future use'
          ]}
        ]
      },
      {
        id: 'feat-draft',
        title: 'Save Draft & Resume Later',
        content: [
          { type: 'list', items: [
            'Use Save draft in the header (or in Step 5) to save your work without confirming',
            'Drafts are stored and appear in My Orders with a "Draft" badge',
            'Click Resume on a draft to continue editing — you will return to the exact step where you clicked Save draft',
            'Delete a draft using the ✕ button in My Orders — a confirmation popup appears before deletion',
            'When you confirm a draft, it becomes a confirmed order and an email is sent',
            'Drafts do not trigger email notifications'
          ]}
        ]
      }
    ]
  },
  {
    id: 'quick-ref',
    title: '6. Quick Reference',
    content: [
      { type: 'table', headers: ['What do you want to do?', 'Where?'], rows: [
        ['Create a new packing list', 'Start at Step 1 after logging in'],
        ['Save work in progress', 'Header > Save draft (or Step 5 > Save draft)'],
        ['Resume a draft', 'Header > My Orders > Draft > Resume (returns to step where draft was saved)'],
        ['Delete a draft', 'Header > My Orders > Draft > ✕ (delete button) — confirmation required'],
        ['Reuse an address', 'Step 1 > Address Book'],
        ['Add a shipping unit', 'Step 2 > Add Unit'],
        ['Search and add a product', 'Step 3 > Add Product'],
        ['Add more of an existing E-type product', 'Step 3 > Add Product > select product > popup to enter quantity'],
        ['Create a custom product', 'Step 3 > Product Selection > Custom Product'],
        ['Print a packing list', 'Step 4 > Print'],
        ['Save a packing list', 'Step 5 > Confirm and Save'],
        ['Save as draft', 'Step 5 > Save draft'],
        ['Email notification after confirm', 'Automatic — sent when packing list is confirmed in Step 5'],
        ['View my own orders and drafts', 'Header > My Orders'],
        ['View all orders (admin)', 'Admin Panel > Orders'],
        ['Mark order as Handled', 'Admin Panel > Orders > order detail > Excel (Both Sheets) — sets status automatically'],
        ['Manage saved addresses (admin)', 'Admin Panel > Addresses'],
        ['Add a product to the catalog', 'Admin Panel > Products'],
        ['Set product type (E, C, P, U)', 'Admin Panel > Products > Edit'],
        ['Create a new user account', 'Admin Panel > Users'],
        ['View login/logout activity', 'Admin Panel > Login Activity'],
        ['Mark session as logged out', 'Admin Panel > Login Activity > Mark logged out'],
        ['Export data', 'Admin Panel > Export'],
        ['Configure notification email (To/CC)', 'Admin Panel > Settings'],
        ['Read the user guide', 'Header > Guides > User Guide'],
        ['Switch theme', 'Header > Day/Night Mode toggle'],
        ['Log out', 'Header > Logout']
      ]}
    ]
  }
];

// ── Render content blocks ──
function ContentBlock({ block, c, searchTerm }) {
  const highlight = (text) => {
    if (!searchTerm) return text;
    const idx = text.toLowerCase().indexOf(searchTerm.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: c.accent, color: '#000', borderRadius: 2, padding: '0 2px' }}>
          {text.slice(idx, idx + searchTerm.length)}
        </mark>
        {text.slice(idx + searchTerm.length)}
      </>
    );
  };

  switch (block.type) {
    case 'text':
      return <p style={{ fontSize: 13, lineHeight: 1.7, color: c.text, margin: '8px 0' }}>{highlight(block.text)}</p>;
    case 'heading':
      return <h4 style={{ fontSize: 13, fontWeight: 600, color: c.accent, margin: '16px 0 6px', letterSpacing: 0.3 }}>{highlight(block.text)}</h4>;
    case 'note':
      return (
        <div style={{ background: c.infoBg, border: '1px solid ' + c.infoBorder, borderRadius: 4, padding: '10px 14px', margin: '10px 0', fontSize: 12, color: c.info, lineHeight: 1.6 }}>
          {highlight(block.text)}
        </div>
      );
    case 'list':
      return (
        <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
          {block.items.map((item, i) => (
            <li key={i} style={{ fontSize: 12, lineHeight: 1.8, color: c.text }}>{highlight(item)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol style={{ margin: '6px 0', paddingLeft: 20 }}>
          {block.items.map((item, i) => (
            <li key={i} style={{ fontSize: 12, lineHeight: 1.8, color: c.text }}>{highlight(item)}</li>
          ))}
        </ol>
      );
    case 'table':
      return (
        <div style={{ overflowX: 'auto', margin: '10px 0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, fontFamily: 'monospace', color: c.textSecondary, letterSpacing: 1, borderBottom: '2px solid ' + c.border, background: c.bgInput, whiteSpace: 'nowrap' }}>
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: '7px 12px', borderBottom: '1px solid ' + c.border, color: ci === 0 ? c.text : c.textSecondary, fontWeight: ci === 0 ? 500 : 400 }}>
                      {highlight(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

// ── Sidebar nav item ──
function NavItem({ section, activeId, onSelect, c, expanded, onToggle, depth = 0, searchTerm }) {
  const isActive = activeId === section.id;
  const hasSubs = section.subsections && section.subsections.length > 0;
  const isExpanded = expanded[section.id];

  const matchesSearch = !searchTerm || sectionMatchesSearch(section, searchTerm);
  if (searchTerm && !matchesSearch) return null;

  return (
    <>
      <div
        onClick={() => {
          if (hasSubs) onToggle(section.id);
          onSelect(section.id);
        }}
        style={{
          padding: '6px 12px',
          paddingLeft: 12 + depth * 16,
          cursor: 'pointer',
          fontSize: depth === 0 ? 12 : 11,
          fontWeight: isActive ? 600 : 400,
          color: isActive ? c.accent : c.text,
          background: isActive ? c.accentBg : 'transparent',
          borderLeft: isActive ? '2px solid ' + c.accent : '2px solid transparent',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          transition: 'all .15s',
          borderRadius: '0 3px 3px 0'
        }}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = c.bgInput; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
      >
        {hasSubs && (
          <span style={{ display: 'flex', alignItems: 'center', fontSize: 10, flexShrink: 0, color: c.textSecondary }}>
            {isExpanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {section.title}
        </span>
      </div>
      {hasSubs && isExpanded && section.subsections.map((sub) => (
        <NavItem key={sub.id} section={sub} activeId={activeId} onSelect={onSelect} c={c} expanded={expanded} onToggle={onToggle} depth={depth + 1} searchTerm={searchTerm} />
      ))}
    </>
  );
}

// ── Search helper ──
function sectionMatchesSearch(section, term) {
  const t = term.toLowerCase();
  if (section.title.toLowerCase().includes(t)) return true;
  if (section.content) {
    for (const block of section.content) {
      if (block.text && block.text.toLowerCase().includes(t)) return true;
      if (block.items && block.items.some((i) => i.toLowerCase().includes(t))) return true;
      if (block.rows && block.rows.some((r) => r.some((c) => c.toLowerCase().includes(t)))) return true;
    }
  }
  if (section.subsections) {
    for (const sub of section.subsections) {
      if (sectionMatchesSearch(sub, term)) return true;
    }
  }
  return false;
}

// ── Main Modal ──
export function UserGuideModal({ isOpen, onClose }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const contentRef = useRef(null);
  const [activeId, setActiveId] = useState('login');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({ 'packing-list': true, admin: false, features: false });

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setActiveId('login');
    }
  }, [isOpen]);

  // Auto-expand sections when searching
  useEffect(() => {
    if (search) {
      const allExpanded = {};
      GUIDE_SECTIONS.forEach((s) => { allExpanded[s.id] = true; });
      setExpanded(allExpanded);
    }
  }, [search]);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectSection = (id) => {
    setActiveId(id);
    if (contentRef.current) {
      const el = contentRef.current.querySelector(`[data-section="${id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Find the active section data
  const findSection = (id) => {
    for (const s of GUIDE_SECTIONS) {
      if (s.id === id) return s;
      if (s.subsections) {
        const sub = s.subsections.find((ss) => ss.id === id);
        if (sub) return sub;
      }
    }
    return GUIDE_SECTIONS[0];
  };

  // Filtered sections for search
  const filteredSections = search
    ? GUIDE_SECTIONS.filter((s) => sectionMatchesSearch(s, search))
    : GUIDE_SECTIONS;

  if (!isOpen) return null;

  const activeSection = findSection(activeId);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: c.overlay,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: c.bg,
          border: '1px solid ' + c.border,
          width: '100%',
          maxWidth: 1050,
          height: '88vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 6,
          overflow: 'hidden',
          boxShadow: '0 20px 60px ' + c.shadow
        }}
      >
        {/* ── Top Bar ── */}
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid ' + c.border,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: c.bgCard,
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              background: c.accentBg,
              border: '1px solid ' + c.accentBorder,
              borderRadius: 4,
              width: 30,
              height: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FiBook size={15} color={c.accent} />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: c.text }}>TMS PLG User Guide</div>
              <div style={{ fontSize: 10, color: c.textSecondary, fontFamily: 'monospace', letterSpacing: 0.8 }}>PACKING LIST GENERATOR DOCUMENTATION</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              ...theme.btnG,
              padding: '5px 10px',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30
            }}
          >
            <FiX size={14} />
          </button>
        </div>

        {/* ── Body: Sidebar + Content ── */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* ── Sidebar ── */}
          <div style={{
            width: 260,
            flexShrink: 0,
            borderRight: '1px solid ' + c.border,
            display: 'flex',
            flexDirection: 'column',
            background: c.bgCard
          }}>
            {/* Search */}
            <div style={{ padding: '12px 12px 8px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: c.bgInput,
                border: '1px solid ' + c.border,
                borderRadius: 4,
                padding: '6px 10px'
              }}>
                <FiSearch size={13} color={c.textSecondary} style={{ flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search guide..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: c.text,
                    fontSize: 12,
                    width: '100%',
                    fontFamily: 'inherit'
                  }}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: c.textSecondary }}
                  >
                    <FiX size={12} />
                  </button>
                )}
              </div>
            </div>

            {/* Nav label */}
            <div style={{ padding: '8px 14px 4px', fontSize: 9, fontFamily: 'monospace', letterSpacing: 1.5, color: c.textSecondary, fontWeight: 600 }}>
              IN THIS GUIDE
            </div>

            {/* Nav items */}
            <div style={{ flex: 1, overflow: 'auto', paddingBottom: 12 }}>
              {filteredSections.map((section) => (
                <NavItem
                  key={section.id}
                  section={section}
                  activeId={activeId}
                  onSelect={selectSection}
                  c={c}
                  expanded={expanded}
                  onToggle={toggleExpand}
                  searchTerm={search}
                />
              ))}
              {filteredSections.length === 0 && (
                <div style={{ padding: '20px 14px', fontSize: 12, color: c.textSecondary, textAlign: 'center' }}>
                  No results found
                </div>
              )}
            </div>
          </div>

          {/* ── Content ── */}
          <div ref={contentRef} style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
            {/* Breadcrumb */}
            <div style={{ fontSize: 11, color: c.textSecondary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>User Guide</span>
              <FiChevronRight size={10} />
              <span style={{ color: c.text }}>{activeSection.title}</span>
            </div>

            {/* Section Title */}
            <h2 style={{ fontSize: 22, fontWeight: 700, color: c.text, margin: '0 0 20px', paddingBottom: 12, borderBottom: '2px solid ' + c.accent }} data-section={activeSection.id}>
              {activeSection.title}
            </h2>

            {/* Section Content */}
            {activeSection.content && activeSection.content.map((block, i) => (
              <ContentBlock key={i} block={block} c={c} searchTerm={search} />
            ))}

            {/* Subsections inline */}
            {activeSection.subsections && activeSection.subsections.map((sub) => (
              <div key={sub.id} data-section={sub.id} style={{ marginTop: 28 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: c.text, margin: '0 0 12px', paddingBottom: 8, borderBottom: '1px solid ' + c.border }}>
                  {sub.title}
                </h3>
                {sub.content && sub.content.map((block, i) => (
                  <ContentBlock key={i} block={block} c={c} searchTerm={search} />
                ))}
              </div>
            ))}

            {/* Footer */}
            <div style={{ marginTop: 40, paddingTop: 16, borderTop: '1px solid ' + c.border, fontSize: 11, color: c.textSecondary }}>
              TMS Packing List Generator — User Guide v1.2
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
